import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldAlert } from "lucide-react";
import { useTherapist } from "../../../context/useTherapist";

const STORAGE_KEY = "mfa_modal_shown";

export default function MfaGdprModal() {
  const { t } = useTranslation();
  const { therapist, loadingTherapist } = useTherapist();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isOnProfilePage = location.pathname === "/profile";

  useEffect(() => {
    if (loadingTherapist) return;
    if (!therapist) return;
    if (therapist.mfa_enabled) return;
    if (therapist.auth_provider === "google") return;

    // Wait until the interactive training demo is completed
    if (!therapist.has_completed_training) return;

    // Don't show on the profile page so the user can set up MFA freely
    if (isOnProfilePage) {
      setIsOpen(false);
      return;
    }

    // Only show once ever
    if (localStorage.getItem(STORAGE_KEY)) return;

    setIsOpen(true);
  }, [therapist, loadingTherapist, isOnProfilePage]);

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setIsOpen(false);
  };

  const handleSetupMfa = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setIsOpen(false);
    navigate("/profile?tab=account");
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm"
            onClick={handleDismiss}
          />

          {/* Modal */}
          <motion.div
            key="modal"
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4 pointer-events-none"
          >
            <div className="bg-surface-raised rounded-3xl shadow-2xl border border-gray-200/80 dark:border-white/10 max-w-md w-full p-8 pointer-events-auto relative">
              <div className="flex flex-col items-center text-center gap-5">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-warning-500/10 text-warning-500">
                  <ShieldAlert className="w-8 h-8" />
                </div>

                <div>
                  <h3 className="text-xl font-bold text-content-primary mb-2">
                    {t("mfaGdprModal.title")}
                  </h3>
                  <p className="text-content-tertiary text-sm leading-relaxed">
                    {t("mfaGdprModal.description")}
                  </p>
                </div>

                <button
                  onClick={handleSetupMfa}
                  className="inline-flex items-center justify-center w-full px-6 py-3 rounded-xl bg-brand-500 hover:bg-brand-600 text-white font-semibold text-sm transition-colors shadow-brand-500/30 shadow-lg"
                >
                  {t("mfaGdprModal.action")}
                </button>

                <button
                  onClick={handleDismiss}
                  className="text-sm text-content-tertiary hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                >
                  {t("mfaGdprModal.dismiss")}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
